import { api, apiFetch } from '@/services/api';

/**
 * ESPELHO EXATO de `QualityGateScores` (backend/app/schemas/seo_engine.py).
 *
 * Os nomes aqui estavam TODOS errados — `gate_01_first_crawl_readiness` em vez
 * de `first_crawl_readiness_score`, e assim por diante nos seis, mais
 * `overall_quality_pass` em vez de `passed_all_gates`. A gaveta renderizava
 * `undefined` em cada portão.
 *
 * Como o backend tambem chamava o avaliador com nomes inexistentes e devolvia
 * 500, os dois lados estavam quebrados ao mesmo tempo — e nenhum teste tocava
 * o caminho. Ao mexer aqui, confira o schema; e o contrato, nao uma sugestao.
 */
export interface QualityGateResponse {
  first_crawl_readiness_score: number;
  discover_clickbait_risk: number;
  original_content_score_est: number;
  featured_image_min_width: number;
  site_focus_radius_delta: number;
  goodclicks_potential_score: number;
  passed_all_gates: boolean;
}

export interface GroundedSource {
  title: string;
  publisher: string;
  url: string | null;
  snippet: string;
  citation_anchor_text: string;
}

export interface HeadlineRecommendation {
  headline_recomendada: string;
  tamanho: number;
  passa_regua: boolean;
  motivos: string[];
}

export interface DraftAnalysisResult {
  quality_gates: QualityGateResponse;
  sources: GroundedSource[];
  headline_recommendation: HeadlineRecommendation;
  paa_questions: string[];
  lead_word_count: number;
  first_crawl_ssr_ready: boolean;
}

export interface InternalLinkSuggestion {
  anchor_text: string;
  target_url: string;
  target_title: string;
  target_post_id: number;
  relevance_score: number;
  context_sentence: string;
  entity_key: string;
  anti_cannibalization_passed: boolean;
}

export interface ImageOptimizationResult {
  ok: boolean;
  original_width?: number;
  original_height?: number;
  final_width?: number;
  final_height?: number;
  passes_discover_gate?: boolean;
  size_bytes?: number;
  format?: string;
  url_persistida?: string | null;
  warning?: string | null;
  error?: string;
}

export interface InfographicResult {
  ok: boolean;
  width?: number;
  height?: number;
  format?: string;
  size_bytes?: number;
  error?: string;
}

export interface DiffChunk {
  type: 'unchanged' | 'addition' | 'deletion';
  text: string;
}

export interface DiffResult {
  has_changes: boolean;
  diff_chunks: DiffChunk[];
  original: string;
  proposed: string;
}

export interface ArticleVersionItem {
  id: number;
  draft_id: string;
  version_number: number;
  version_type: 'human_seed' | 'ai_proposal' | 'accepted_revision';
  title?: string;
  lead?: string;
  content?: string;
  diff_payload?: unknown;
  author_id?: string;
  created_at?: string;
}

export interface NewsStorySchema {
  titulo: string;
  excerpt: string;
  lead: string;
  corpo_html: string;
  perguntas_leitor: string[];
  fontes: Array<{ title: string; publisher: string; citation_anchor_text: string }>;
  links_internos: Array<{ anchor: string; url: string; title: string }>;
  imagem: {
    url_persistida: string | null;
    largura: number;
    altura: number;
    elegivel_discover: boolean;
  };
  veredicto_manchete: { ok: boolean; chars: number; problems: string[] };
  veredicto_excerpt?: { ok: boolean; chars: number; problems: string[] };
  portoes: Record<string, unknown>;
  trilha: string;
  geracao: {
    modo: 'ia' | 'formatador';
    modelo: string | null;
    tokens_in: number;
    tokens_out: number;
    custo_usd: number;
    duracao_ms: number;
  };
  origem: {
    tipo_insumo: 'texto' | 'pdf' | 'docx' | 'audio' | 'json';
    nome_arquivo: string | null;
    intel_id: string | null;
  };
  title: string;
  body_html: string;
  paa_questions?: string[];
  quality_gates?: Record<string, unknown>;
}

export interface CopilotErrorDetail {
  tipo?: 'aviso_editorial' | 'erro_tecnico';
  titulo?: string;
  erro?: string;
  motivo?: string;
  sugestoes?: string[];
  veredicto?: {
    ok: boolean;
    numeros_inventados?: string[];
    entidades_inventadas?: string[];
    frases_proibidas?: string[];
    dominios_nao_autorizados?: string[];
  };
}

export type ParameterizedArticleResult = NewsStorySchema;

export interface FormatTechnicalResult extends NewsStorySchema {
  headline_verdict: { ok: boolean; chars: number; problems: string[] };
}

export interface DocumentIngestResult {
  filename: string;
  size_bytes: number;
  text_content: string;
  tipo_insumo?: 'texto' | 'pdf' | 'docx' | 'audio' | 'json';
  transcricao_usd?: number;
}

export const copilotApi = {
  async analyze(draft: {
    title: string;
    lead: string;
    content: string;
    topic_query?: string;
  }): Promise<DraftAnalysisResult> {
    return api.post<DraftAnalysisResult>('/copilot/analyze', draft);
  },

  async suggestInternalLinks(draftText: string, site?: string): Promise<InternalLinkSuggestion[]> {
    const res = await api.post<{ suggestions: InternalLinkSuggestion[] }>(
      '/copilot/internal-links',
      { draft_text: draftText, site },
    );
    return res.suggestions || [];
  },

  async optimizeImage(file: File): Promise<ImageOptimizationResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch<ImageOptimizationResult>('/copilot/optimize-image', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  async generateInfographic(payload: {
    title: string;
    headline_overlay: string;
    brand_slug?: string;
  }): Promise<InfographicResult> {
    return api.post<InfographicResult>('/copilot/generate-infographic', payload);
  },

  async proposeDiff(originalText: string, proposedText: string): Promise<DiffResult> {
    return api.post<DiffResult>('/copilot/propose-diff', {
      original_text: originalText,
      proposed_text: proposedText,
    });
  },

  async saveVersion(payload: {
    draft_id: string;
    version_type: 'human_seed' | 'ai_proposal' | 'accepted_revision';
    title?: string;
    lead?: string;
    content?: string;
    diff_payload?: unknown;
  }): Promise<ArticleVersionItem> {
    return api.post<ArticleVersionItem>('/copilot/versions', payload);
  },

  async listVersions(draftId: string): Promise<ArticleVersionItem[]> {
    return api.get<ArticleVersionItem[]>(`/copilot/versions/${draftId}`);
  },

  async generateParameterizedArticle(payload: {
    raw_input: string;
    context_docs?: string;
    site?: string;
  }): Promise<ParameterizedArticleResult> {
    return api.post<ParameterizedArticleResult>('/copilot/generate', payload);
  },

  async formatTechnicalDraft(payload: {
    raw_text: string;
    headline_fallback?: string;
  }): Promise<FormatTechnicalResult> {
    return api.post<FormatTechnicalResult>('/copilot/format-technical', payload);
  },

  async ingestDocument(file: File): Promise<DocumentIngestResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch<DocumentIngestResult>('/copilot/ingest-document', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  async previewPayload(payload: {
    story: Record<string, unknown>;
    site?: string;
  }): Promise<Record<string, unknown>> {
    return api.post<Record<string, unknown>>('/copilot/preview-payload', payload);
  },

  async publishArticle(payload: {
    story: Record<string, unknown>;
    site?: string;
    status?: string;
  }): Promise<{ ok: boolean; post_id: number; url: string; redirect_url: string }> {
    return api.post<{ ok: boolean; post_id: number; url: string; redirect_url: string }>(
      '/copilot/publish',
      payload,
    );
  },
};
